import {
  type GachaEnvironment,
  type GachaEvmChain,
  getGachaEnvironment,
  getGachaEvmChains,
  getGachaPreferredToken,
} from "@anaconda/core/gacha/evm-chains"
import {
  type GachaEthPaymentInput,
  encodeGachaEthPayment,
  gachaAuthenticatedEthPaymentQuoteSchema,
  gachaEthPaymentInputSchema,
  gachaEthPaymentQuoteSchema,
  getGachaEthPaymentDeployment,
  supportsGachaEthPayment,
  validateGachaEthPaymentQuote,
} from "@anaconda/core/gacha/evm-eth-payment"
import {
  type GachaEvmPurchase,
  gachaEvmPayAbi,
  gachaEvmPurchaseSchema,
  isGachaEvmDelivered,
} from "@anaconda/core/gacha/evm-purchase"
import { wrap } from "@reatom/core"
import Big from "big.js"
import {
  type Address,
  BaseError,
  type Hex,
  UserRejectedRequestError,
  encodeFunctionData,
  erc20Abi,
} from "viem"
import { z } from "zod"
import { type GachaApiOptions, getGachaApiUrl } from "./gacha-api"
import { type GachaMachineView, mapOpenPackResult } from "./gacha-data"
import {
  GachaEthPaymentRecoveryError,
  type GachaEthPaymentTracking,
  type GachaEthSubmission,
  createGachaEthPaymentTracking,
} from "./gacha-eth-payment-tracking"
import type {
  GachaExecution,
  GachaPullResult,
  GachaPurchaseOutcome,
} from "./gacha-execution"
import { isGachaMachineExcluded } from "./gacha-machine-exclusions"
import { shouldRequireInstantBuybackForLowCommonStock } from "./gacha-purchase-dialog"
import { isGachaWalletCancellationMessage } from "./gacha-wallet-cancellation"

export type GachaEvmSigningWallet = {
  address: Address
  getChainId: () => Promise<number>
  authorizeChain?: () => Promise<void>
  send: (to: Address, data: Hex, value?: bigint) => Promise<Hex>
}
export type GachaEvmPaymentRpc = {
  getChainId: () => Promise<number>
  getFunds: (
    owner: Address,
    token: Address,
    spender: Address,
  ) => Promise<{
    tokenBalance: bigint
    nativeBalance: bigint
    allowance: bigint
    decimals: number
  }>
  estimateCost: (
    from: Address,
    to: Address,
    data: Hex,
    value?: bigint,
  ) => Promise<bigint>
  wait: (hash: Hex) => Promise<boolean>
}
function isEvmGachaRevealReady(record: GachaEvmPurchase) {
  const award = record.award
  return Boolean(
    record.evidence &&
      record.evidence.chainId === record.chain.chainId &&
      record.state === "fulfilling" &&
      record.memo &&
      award?.memo === record.memo &&
      award.chain_id === record.chain.chainId &&
      award.evm_contract_address?.toLowerCase() ===
        record.chain.cardContract.toLowerCase() &&
      award.rarity &&
      (award.nftWon?.name?.trim() || award.card_name?.trim()),
  )
}

export function normalizeEvmGachaResult(
  record: GachaEvmPurchase,
  machine: GachaMachineView,
): GachaPullResult {
  const award = record.award
  const delivered =
    record.state === "delivered" &&
    Boolean(record.evidence) &&
    Boolean(award && isGachaEvmDelivered(200, award, record))
  if (!award || (!delivered && !isEvmGachaRevealReady(record)))
    throw new Error("Reveal data or NFT delivery is not confirmed")
  const result = mapOpenPackResult(
    {
      memo: record.memo,
      rarity: award.rarity,
      nftWon: {
        ...award.nftWon,
        name: award.nftWon?.name ?? award.card_name,
        insured_value: award.insured_value ?? award.nftWon?.insured_value,
      },
      buybackAmount: award.buyback_amount,
    },
    machine,
  )
  return {
    ...result,
    buybackOffer: award.buyback_amount == null ? "—" : result.buybackOffer,
    contractAddress: record.chain.cardContract,
    nftAddress: undefined,
    chainId: record.chain.chainId,
    tokenId: delivered ? (award.evm_token_id ?? undefined) : undefined,
    purchaseId: record.id,
    deliveryPending: !delivered,
    purchaseWalletAddress: record.walletAddress,
    paymentToken: getGachaPreferredToken(record.chain).symbol,
    paymentTokenAddress: getGachaPreferredToken(record.chain).address,
  }
}
export function createEvmGachaExecution({
  chain,
  environment,
  wallet,
  rpc,
  apiOptions,
  getIdentityToken,
  onRecord,
  onPending = () => {},
  onSigning = () => {},
  getPending,
  ethPaymentTracking = createGachaEthPaymentTracking(
    "gacha.execution.ethPayments",
  ),
  refresh,
  delay = (ms) => new Promise((resolve) => globalThis.setTimeout(resolve, ms)),
}: {
  chain: GachaEvmChain
  environment: GachaEnvironment
  wallet: GachaEvmSigningWallet
  rpc: GachaEvmPaymentRpc
  apiOptions: GachaApiOptions
  getIdentityToken: () => Promise<string | null>
  onRecord: (record: GachaEvmPurchase) => void
  onPending?: (pending: boolean) => void
  // Foreground queue ownership only; durable recovery never owns selection.
  onSigning?: (signing: boolean) => void
  getPending?: () => boolean
  ethPaymentTracking?: GachaEthPaymentTracking
  refresh: (record: GachaEvmPurchase) => void
  delay?: (ms: number) => Promise<void>
}): GachaExecution {
  let running = false
  let unresolved = false
  const hasUnresolvedPurchase = () => {
    // Activity/recovery may resolve a blocker after this executor stops polling.
    // Clear when no record blocking a new purchase remains.
    if (unresolved && getPending?.() === false) unresolved = false
    return unresolved
  }
  const token = getGachaPreferredToken(chain)
  const ethPaymentSupported =
    supportsGachaEthPayment(chain) &&
    getGachaEvmChains(environment).some(
      (configured) => configured.chainId === chain.chainId,
    )
  const recoverEthSubmission = async () => {
    const submission = ethPaymentTracking.get(wallet.address, chain.chainId)
    if (!submission) return
    if (!submission.hash)
      throw new GachaEthPaymentRecoveryError("unknown", submission)
    if ((await wrap(rpc.getChainId())) !== chain.chainId)
      throw new Error("Conversion RPC chain mismatch")
    let confirmed: boolean
    try {
      confirmed = await wrap(rpc.wait(submission.hash))
    } catch {
      throw new GachaEthPaymentRecoveryError("pending", submission)
    }
    ethPaymentTracking.clear(submission)
    if (confirmed)
      throw new GachaEthPaymentRecoveryError("confirmed", submission)
  }
  let pinned: GachaEvmPurchase | undefined
  let reservationSubmitted = false
  let rejectionToken: string | undefined
  const request = async (path: string, body: object) => {
    const identityToken = await wrap(getIdentityToken())
    if (!identityToken)
      throw new Error("Verified linked-wallet identity is required to purchase")
    if (path === "/purchases") reservationSubmitted = true
    const response = await wrap(
      fetch(getGachaApiUrl(`/evm${path}`, apiOptions), {
        method: "POST",
        cache: "no-store",
        signal: AbortSignal.timeout(30_000),
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${identityToken}`,
        },
        body: JSON.stringify({ ...body, walletAddress: wallet.address }),
      }),
    )
    if (
      path === "/purchases" &&
      (response.status === 401 || response.status === 403)
    )
      reservationSubmitted = false
    if (!response.ok) {
      let message = `Purchase request failed (HTTP ${response.status}).`
      try {
        const error = z
          .object({ error: z.string() })
          .safeParse(await wrap(response.json()))
        if (error.success) message = error.data.error
      } catch {
        // An invalid error body must not hide the failed HTTP status.
      }
      throw new Error(message)
    }
    const { rejectionToken: grantedRejectionToken, ...record } =
      gachaEvmPurchaseSchema
        .extend({ rejectionToken: z.string().uuid().optional() })
        .parse(await wrap(response.json()))
    if (path.endsWith("/payment-intent")) rejectionToken = grantedRejectionToken
    if (
      record.walletAddress.toLowerCase() !== wallet.address.toLowerCase() ||
      record.environment !== environment ||
      record.chain.chainId !== chain.chainId ||
      record.chain.paymentContract.toLowerCase() !==
        chain.paymentContract.toLowerCase() ||
      record.chain.cardContract.toLowerCase() !==
        chain.cardContract.toLowerCase() ||
      getGachaPreferredToken(record.chain).address.toLowerCase() !==
        token.address.toLowerCase() ||
      getGachaPreferredToken(record.chain).decimals !== token.decimals
    )
      throw new Error("Purchase context changed; signing blocked")
    if (
      getGachaEnvironment(record.upstream) !== environment ||
      record.chain.vaultContract.toLowerCase() !==
        chain.vaultContract.toLowerCase() ||
      record.chain.mintGuardContract.toLowerCase() !==
        chain.mintGuardContract.toLowerCase() ||
      getGachaPreferredToken(record.chain).key !== token.key ||
      getGachaPreferredToken(record.chain).symbol !== token.symbol ||
      (pinned &&
        (record.id !== pinned.id ||
          record.memo !== pinned.memo ||
          record.amount !== pinned.amount ||
          record.machineCode !== pinned.machineCode))
    )
      throw new Error("Original Issued Pack context mismatch")
    if (
      record.state === "delivered" &&
      (!record.evidence ||
        !record.award ||
        !isGachaEvmDelivered(response.status, record.award, record))
    )
      throw new Error("NFT delivery is not confirmed")
    pinned ??= record
    // Payment recovery remains active, but a separately authorized purchase may
    // proceed once this foreground queue stops. Unknown reservations stay blocked.
    unresolved =
      record.active &&
      !["payment_failed", "payment_pending"].includes(record.state)
    onRecord(record)
    // A delivered pack is not the end of an authorized multi-pack sequence.
    onPending(running || unresolved)
    return record
  }
  const assertChain = async () => {
    if (
      (await wrap(wallet.getChainId())) !== chain.chainId ||
      (await wrap(rpc.getChainId())) !== chain.chainId
    )
      throw new Error(
        `Switch the purchase wallet to ${chain.name} before signing`,
      )
  }
  const requiredAmount = (machine: GachaMachineView, quantity: number) => {
    if (!Number.isFinite(machine.price) || machine.price <= 0)
      throw new Error(
        "Pack price is unavailable. Please refresh and try again.",
      )
    return BigInt(
      new Big(machine.price)
        .times(new Big(10).pow(token.decimals))
        .round(0, Big.roundUp)
        .times(quantity)
        .toFixed(0),
    )
  }
  const createEthQuoteContext = (
    machine: GachaMachineView,
    quantity: number,
  ): GachaEthPaymentInput =>
    gachaEthPaymentInputSchema.parse({
      chainId: chain.chainId,
      walletAddress: wallet.address,
      requiredAmountBase: requiredAmount(machine, quantity).toString(),
    })
  const assertPurchaseFunds = async (
    machine: GachaMachineView,
    quantity: number,
  ) => {
    if (!Number.isFinite(machine.price) || machine.price <= 0)
      throw new Error(
        "Pack price is unavailable. Please refresh and try again.",
      )
    const funds = await wrap(
      rpc.getFunds(wallet.address, token.address, chain.paymentContract),
    )
    if (funds.decimals !== token.decimals)
      throw new Error("Payment token decimals mismatch")
    // The displayed total is a preflight estimate, never a payment amount.
    // Keep the exact reservation amount checks before approval and payment.
    const displayedTotal = requiredAmount(machine, quantity)
    if (funds.tokenBalance < displayedTotal)
      throw new Error(
        `Insufficient ${token.symbol} in the purchase wallet on ${chain.name}`,
      )
    if (funds.nativeBalance === BigInt(0))
      throw new Error(`Native ETH is required for gas on ${chain.name}`)
  }
  const validatePurchase: GachaExecution["validatePurchase"] = ({
    machine,
    quantity,
  }) => {
    if (isGachaMachineExcluded(machine.code, chain.chainId))
      throw new Error("This machine is unavailable on the selected network")
    if (!Number.isInteger(quantity) || quantity < 1 || quantity > 10)
      throw new Error("Choose between one and ten packs")
    if (!machine.isOpen || machine.isSoldOut || machine.stockTotal < quantity)
      throw new Error("This machine is unavailable")
    if (
      shouldRequireInstantBuybackForLowCommonStock({
        machineCode: machine.code,
        commonStock: machine.commonStock,
        purchaseQuantity: quantity,
        instantBuybackEnabled: false,
      })
    )
      throw new Error(
        "Cards are running low. This pack is unavailable to purchase.",
      )
    if (running || hasUnresolvedPurchase())
      throw new Error(
        "An Issued Pack is pending. Wait before purchasing again.",
      )
  }
  return {
    walletAddress: wallet.address,
    validatePurchase,
    ...(ethPaymentSupported
      ? {
          acknowledgeUnknownEthPayment: (id: string) =>
            ethPaymentTracking.acknowledgeUnknown(
              wallet.address,
              chain.chainId,
              id,
            ),
          checkEthPayment: recoverEthSubmission,
          quoteEthPayment: async (
            input: Parameters<GachaExecution["validatePurchase"]>[0],
          ) => {
            validatePurchase(input)
            await wrap(recoverEthSubmission())
            const identityToken = await wrap(getIdentityToken())
            if (!identityToken)
              throw new Error(
                "Verified linked-wallet identity is required to quote",
              )
            const context = createEthQuoteContext(input.machine, input.quantity)
            const response = await wrap(
              fetch(getGachaApiUrl("/evm/eth-payment/quote", apiOptions), {
                method: "POST",
                cache: "no-store",
                signal: AbortSignal.timeout(15000),
                headers: {
                  Authorization: `Bearer ${identityToken}`,
                  "Content-Type": "application/json",
                },
                body: JSON.stringify(context),
              }),
            )
            const data = await wrap(response.json())
            if (!response.ok) {
              const error = z
                .object({ message: z.string().optional() })
                .safeParse(data)
              throw new Error(
                error.success
                  ? (error.data.message ?? "ETH quote unavailable")
                  : "ETH quote unavailable",
              )
            }
            const authenticated =
              gachaAuthenticatedEthPaymentQuoteSchema.parse(data)
            const quote = validateGachaEthPaymentQuote(
              authenticated.quote,
              context,
            )
            return {
              userId: authenticated.userId,
              quote,
              machineCode: input.machine.code,
              quantity: input.quantity,
            }
          },
        }
      : {}),
    async purchase({
      machine,
      quantity,
      instantBuyback,
      onProgress,
      ethPayment,
      privyId,
      onEthTransactionConfirmed,
    }) {
      validatePurchase({ machine, quantity, instantBuyback })
      if (ethPayment && ethPayment.userId !== privyId)
        throw new Error("ETH payment user changed. Request a new quote.")
      running = true
      onSigning(true)
      const results: GachaPullResult[] = []
      const unsignedReservations: GachaEvmPurchase[] = []
      let remainingAmount = BigInt(0)
      const outcome = (
        pending = false,
        cancelled = false,
      ): GachaPurchaseOutcome => ({
        results,
        completedPacks: results.length,
        cancelled,
        turboBuybacks: 0,
        turboBuybackTotal: 0,
        ...(pending || results.some((result) => result.deliveryPending)
          ? { pending: true }
          : {}),
      })
      try {
        await wrap(recoverEthSubmission())
        if (wallet.authorizeChain) await wrap(wallet.authorizeChain())
        await wrap(assertChain())
        if (ethPayment) {
          if (
            !ethPaymentSupported ||
            ethPayment.machineCode !== machine.code ||
            ethPayment.quantity !== quantity
          )
            throw new Error(
              "ETH payment pack or quantity changed. Request a new quote.",
            )
          const context = createEthQuoteContext(machine, quantity)
          const quote = validateGachaEthPaymentQuote(
            gachaEthPaymentQuoteSchema.parse(ethPayment.quote),
            context,
          )
          const deployment = getGachaEthPaymentDeployment(quote.chainId)
          const data = encodeGachaEthPayment(quote)
          const value = BigInt(quote.maxInputAmountWei)
          const funds = await wrap(
            rpc.getFunds(wallet.address, token.address, chain.paymentContract),
          )
          if (funds.decimals !== token.decimals)
            throw new Error("Payment token decimals mismatch")
          if (funds.nativeBalance < value)
            throw new Error(
              "Not enough ETH balance for the maximum conversion.",
            )
          const gas = await wrap(
            rpc.estimateCost(wallet.address, deployment.router, data, value),
          )
          if (funds.nativeBalance < value + gas)
            throw new Error("Insufficient ETH for conversion and gas")
          validateGachaEthPaymentQuote(quote, context)
          await wrap(assertChain())
          onProgress("Confirm ETH conversion")
          const submission: GachaEthSubmission = {
            id: globalThis.crypto.randomUUID(),
            userId: privyId,
            walletAddress: wallet.address,
            chainId: quote.chainId,
          }
          ethPaymentTracking.begin(submission)
          let hash: Hex
          try {
            hash = await wrap(wallet.send(deployment.router, data, value))
          } catch (caught) {
            const rejected =
              caught instanceof UserRejectedRequestError ||
              (caught instanceof BaseError &&
                caught.walk(
                  (error) => error instanceof UserRejectedRequestError,
                ) instanceof UserRejectedRequestError) ||
              (caught instanceof Error &&
                "code" in caught &&
                caught.code === 4001)
            if (rejected) {
              ethPaymentTracking.clear(submission)
              throw caught
            }
            throw new GachaEthPaymentRecoveryError("unknown", submission)
          }
          try {
            ethPaymentTracking.broadcast({ ...submission, hash })
          } catch {
            throw new GachaEthPaymentRecoveryError("unknown", {
              ...submission,
              hash,
            })
          }
          onProgress("Confirming ETH conversion")
          let confirmed: boolean
          try {
            confirmed = await wrap(rpc.wait(hash))
          } catch {
            throw new GachaEthPaymentRecoveryError("pending", {
              ...submission,
              hash,
            })
          }
          ethPaymentTracking.clear(submission)
          if (!confirmed)
            throw new Error("ETH conversion reverted. No packs were purchased.")
          onEthTransactionConfirmed?.()
          await wrap(assertChain())
          const converted = await wrap(
            rpc.getFunds(wallet.address, token.address, chain.paymentContract),
          )
          if (
            converted.decimals !== token.decimals ||
            converted.tokenBalance < requiredAmount(machine, quantity) ||
            converted.tokenBalance - funds.tokenBalance <
              BigInt(quote.outputAmountBase)
          )
            throw new Error(
              `${token.symbol} received could not be verified. No packs were purchased; check your wallet balance.`,
            )
        }
        await wrap(assertPurchaseFunds(machine, quantity))
        onPending(true)
        // Obtain every exact quote before approving a finite batch spending cap.
        // No payment intent is granted until all reservations have been validated.
        for (let index = 0; index < quantity; index++) {
          pinned = undefined
          reservationSubmitted = false
          await wrap(assertChain())
          if (quantity > 1) onProgress(`Creating pack ${index + 1}/${quantity}`)
          const record = await wrap(
            request("/purchases", {
              chainId: chain.chainId,
              machineCode: machine.code,
              requestId: globalThis.crypto.randomUUID(),
            }),
          )
          const duplicate = unsignedReservations.some(
            (reserved) =>
              reserved.id === record.id || reserved.memo === record.memo,
          )
          if (record.state === "reserved" && !duplicate) {
            unsignedReservations.push(record)
            unresolved = false
          }
          if (
            !record.memo ||
            !record.amount ||
            BigInt(record.amount) <= BigInt(0) ||
            record.state !== "reserved" ||
            !record.active ||
            record.machineCode !== machine.code ||
            duplicate
          )
            throw new Error(
              "Original reservation is unresolved; do not pay again",
            )
          remainingAmount += BigInt(record.amount)
        }
        for (let index = 0; index < quantity; index++) {
          const reserved = unsignedReservations.shift()
          if (!reserved) throw new Error("Original reservation is unavailable")
          pinned = reserved
          unresolved = reserved.active
          reservationSubmitted = false
          rejectionToken = undefined
          let record = reserved
          let paymentIntent = false
          let paymentSignatureRequested = false
          try {
            await wrap(assertChain())
            if (index > 0)
              await wrap(assertPurchaseFunds(machine, quantity - index))
            if (!record.memo || !record.amount)
              throw new Error("Original reservation is unavailable")
            const amount = BigInt(record.amount)
            const funds = await wrap(
              rpc.getFunds(
                wallet.address,
                token.address,
                chain.paymentContract,
              ),
            )
            if (funds.decimals !== token.decimals)
              throw new Error("Payment token decimals mismatch")
            if (funds.tokenBalance < remainingAmount)
              throw new Error(
                `Insufficient ${token.symbol} in the purchase wallet on ${chain.name}`,
              )
            if (funds.nativeBalance === BigInt(0))
              throw new Error(`Native ETH is required for gas on ${chain.name}`)
            const payData = encodeFunctionData({
              abi: gachaEvmPayAbi,
              functionName: "pay",
              args: [token.address, amount, record.memo],
            })
            if (funds.allowance < remainingAmount) {
              const approveData = encodeFunctionData({
                abi: erc20Abi,
                functionName: "approve",
                args: [chain.paymentContract, remainingAmount],
              })
              const approvalCost = await wrap(
                rpc.estimateCost(wallet.address, token.address, approveData),
              )
              if (funds.nativeBalance < approvalCost)
                throw new Error(
                  `Insufficient native ETH for approval on ${chain.name}`,
                )
              await wrap(assertChain())
              onProgress(
                quantity > 1
                  ? `Sign ${index + 1}/${quantity}`
                  : "Sign in wallet",
              )
              const approvalHash = await wrap(
                wallet.send(token.address, approveData),
              )
              onProgress("Confirming token approval")
              if (!(await wrap(rpc.wait(approvalHash))))
                throw new Error("Approval was not successful")
            }
            // Estimate pay only after approval; estimating before allowance exists would revert.
            const readyFunds = await wrap(
              rpc.getFunds(
                wallet.address,
                token.address,
                chain.paymentContract,
              ),
            )
            if (readyFunds.decimals !== token.decimals)
              throw new Error("Payment token decimals changed after approval")
            if (readyFunds.allowance < remainingAmount)
              throw new Error(
                "Token approval is not effective yet. Wait for confirmation and try again.",
              )
            if (readyFunds.tokenBalance < remainingAmount)
              throw new Error(
                `Insufficient ${token.symbol} in the purchase wallet`,
              )
            const cost = await wrap(
              rpc.estimateCost(wallet.address, chain.paymentContract, payData),
            )
            if (readyFunds.nativeBalance < cost)
              throw new Error(
                `Insufficient native ETH for payment on ${chain.name}`,
              )
            await wrap(assertChain())
            // Once intent registration is attempted, a lost HTTP response is ambiguous.
            paymentIntent = true
            record = await wrap(
              request(`/purchases/${record.id}/payment-intent`, {}),
            )
            if (record.state !== "payment_pending")
              throw new Error("Payment permission was not granted")
            await wrap(assertChain())
            onProgress(
              quantity > 1 ? `Sign ${index + 1}/${quantity}` : "Sign in wallet",
            )
            let hash: Hex
            try {
              paymentSignatureRequested = true
              hash = await wrap(wallet.send(chain.paymentContract, payData))
            } catch (caught) {
              // This queue will never request another signature, even if the
              // provider's rejection is uncertain and durable recovery continues.
              onSigning(false)
              // Provider cancellation informs this browser, but the BFF cannot
              // attest an untrusted client report or prove nonbroadcast.
              const message =
                caught instanceof Error
                  ? caught.message
                  : typeof caught === "string"
                    ? caught
                    : ""
              if (
                isGachaWalletCancellationMessage(message) ||
                z.object({ code: z.literal(4001) }).safeParse(caught).success ||
                (caught instanceof BaseError &&
                  caught.walk(
                    (error) => error instanceof UserRejectedRequestError,
                  ) instanceof UserRejectedRequestError)
              ) {
                if (rejectionToken) {
                  try {
                    record = await wrap(
                      request(`/purchases/${record.id}/payment-rejected`, {
                        rejectionToken,
                      }),
                    )
                  } catch {
                    // Reporting failure does not change the user's cancellation
                    // or prove nonbroadcast. Keep the original recovery record.
                  }
                }
                return {
                  ...outcome(record.active, true),
                  cancelledByUser: true,
                }
              }
              throw caught
            }
            // Final pay broadcast ends signing ownership before HTTP registration,
            // canonical confirmation or NFT fulfillment. Earlier packs retain it
            // while the authorized sequential queue may still request signatures.
            if (index === quantity - 1) onSigning(false)
            remainingAmount -= amount
            // Registration occurs immediately; a timeout/unknown hash is never unpaid.
            record = await wrap(
              request(`/purchases/${record.id}/payment`, { hash }),
            )
            if (record.evidence) refresh(record)
            onProgress(
              quantity > 1
                ? `Opening pack ${index + 1} of ${quantity}`
                : "Opening pack",
            )
            for (let mintPoll = 0; mintPoll < 20; ) {
              record = await wrap(request(`/purchases/${record.id}/open`, {}))
              if (record.state === "delivered") {
                const result = normalizeEvmGachaResult(record, machine)
                refresh(record)
                results.push(result)
                break
              }
              // Collect reveal-ready cards without waiting for minting. Finish
              // the authorized batch's signatures before returning its reveal.
              if (isEvmGachaRevealReady(record)) {
                results.push(normalizeEvmGachaResult(record, machine))
                break
              }
              // Support is a durable unresolved outcome, not a reason to keep the
              // browser's in-flight guard alive until its local polling limit.
              if (record.state === "needs_support") return outcome(true)
              if (
                record.state === "payment_failed" ||
                record.state === "payment_rejected"
              )
                return outcome(record.active, true)
              // A delayed paid pack ends this shopping queue immediately. Activity
              // may recover this memo, but never authorizes its unstarted remainder.
              if (record.evidence && record.state === "fulfilling") {
                if (quantity > 1) return outcome(true)
                mintPoll++
              }
              // Payment confirmations do not consume the single-pack mint UI budget.
              // Keep waiting on this hash; only real delayed minting stops the queue.
              await wrap(delay(Math.max(500, record.retryAfterMs ?? 1500)))
            }
            // Local UI polling limit is not upstream exhaustion or unpaid termination.
            if (record.state !== "delivered" && !isEvmGachaRevealReady(record))
              return outcome(true)
          } catch (caught) {
            onSigning(false)
            if (record?.state === "reserved" && !paymentIntent) {
              try {
                await wrap(request(`/purchases/${record.id}/cancel-unpaid`, {}))
              } catch {
                /* Durable context must remain visible. */
              }
            } else if (paymentIntent) {
              onPending(true)
              const message =
                caught instanceof Error
                  ? caught.message
                  : typeof caught === "string"
                    ? caught
                    : "Purchase request failed."
              return {
                ...outcome(true),
                errorMessage: paymentSignatureRequested
                  ? `Purchase could not complete. ${message} Check Activity; do not pay again.`
                  : `The purchase transaction was not requested for this pack. ${message} Check Activity before trying again.`,
              }
            }
            if (results.length > 0)
              return {
                ...outcome(unresolved, true),
                ...(caught instanceof Error
                  ? { errorMessage: caught.message }
                  : {}),
              }
            throw caught
          }
        }
        return outcome()
      } catch (caught) {
        if (reservationSubmitted && !pinned) unresolved = true
        throw caught
      } finally {
        onSigning(false)
        // Never cancel a pack for which payment intent may have been granted.
        // Cleanup must not clear an ambiguous reservation or payment blocker.
        let pending =
          unresolved || results.some((result) => result.deliveryPending)
        for (const record of unsignedReservations) {
          pinned = record
          try {
            const cancelled = await wrap(
              request(`/purchases/${record.id}/cancel-unpaid`, {}),
            )
            pending ||= cancelled.active
          } catch {
            pending = true
          }
        }
        unresolved = pending
        running = false
        onSigning(false)
        onPending(unresolved)
      }
    },
  }
}
